/**
 * ChecklistShowcase — Premium interactive checklist renderer.
 *
 * Expected fields: title, subtitle, context, category, status, priority,
 *   due_date, total_items, completed_items, groups, critical_items, tip,
 *   follow_up_chips
 *
 * Checked state is local-only (no backend sync in this version).
 */

import React, { useState, useCallback, useMemo } from 'react';
import { usePresentationBreakpoint, AthenaTip, ActionChips, HeroPhotoPanel, MetricStrip }
  from '../presentation-system';

// ── Color constants ──────────────────────────────────────────────────────────
const C = {
  accent:        '#38bdf8',
  accentFade:    'rgba(56,189,248,0.10)',
  accentBorder:  'rgba(56,189,248,0.22)',
  success:       '#2dd4bf',
  successFade:   'rgba(45,212,191,0.10)',
  successBorder: 'rgba(45,212,191,0.28)',
  warning:       '#f59e0b',
  warningFade:   'rgba(245,158,11,0.09)',
  danger:        '#fb7185',
  dangerFade:    'rgba(251,113,133,0.10)',
  dangerBorder:  'rgba(251,113,133,0.25)',
  surface:       'rgba(14,22,40,0.55)',
  surface2:      'rgba(14,22,40,0.35)',
  border:        'rgba(56,189,248,0.14)',
  text:          'rgba(226,232,240,0.95)',
  textMuted:     'rgba(148,163,184,0.72)',
  textFaint:     'rgba(100,116,139,0.55)',
};

const STATUS_META = {
  done:        { label: 'Concluído',    color: C.success },
  in_progress: { label: 'Em andamento', color: C.accent  },
  not_started: { label: 'Não iniciado', color: C.textMuted },
  blocked:     { label: 'Bloqueado',    color: C.danger  },
};
const PRIORITY_META = {
  critical: { label: 'Crítica', color: C.danger  },
  high:     { label: 'Alta',    color: C.warning },
  medium:   { label: 'Média',   color: C.accent  },
  low:      { label: 'Baixa',   color: C.textMuted },
};

// ── MetricChip ───────────────────────────────────────────────────────────────
function MetricChip({ label, value, color = C.accent }) {
  if (value === null || value === undefined) return null;
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 2,
      padding: '7px 12px',
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 7,
      minWidth: 60,
    }}>
      <span style={{
        fontSize: 17, fontWeight: 800, color,
        lineHeight: 1.1, fontVariantNumeric: 'tabular-nums',
      }}>{value}</span>
      <span style={{
        fontSize: 10, color: C.textFaint,
        textTransform: 'uppercase', letterSpacing: '.07em',
      }}>{label}</span>
    </div>
  );
}

// ── Pills ────────────────────────────────────────────────────────────────────
function Pill({ label, color }) {
  if (!label) return null;
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, letterSpacing: '.04em',
      color,
      background: `${color}18`,
      border: `1px solid ${color}35`,
      padding: '2px 9px', borderRadius: 12,
    }}>{label}</span>
  );
}

// ── Hero Desktop ─────────────────────────────────────────────────────────────
function ChecklistHeroDesktop({ d, checkedCount, total }) {
  const pct     = total > 0 ? Math.round((checkedCount / total) * 100) : 0;
  const sm      = d.status   && STATUS_META[d.status];
  const pm      = d.priority && PRIORITY_META[d.priority];
  const titleFs = (d.title || '').length > 38 ? 21 : (d.title || '').length > 24 ? 26 : 30;
  const METRICS = [
    { icon: '📋', value: total,         label: 'Total'      },
    { icon: '✅', value: checkedCount,  label: 'Concluídos' },
    { icon: '📊', value: `${pct}%`,     label: 'Completo'   },
    pm && { icon: '⚡', value: pm.label, label: 'Prioridade' },
  ].filter(Boolean);

  return (
    <div style={{ background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)', border: `1px solid ${C.border}`,
      borderRadius: 14, overflow: 'hidden', display: 'flex', height: 260, marginBottom: 18,
      boxShadow: '0 4px 40px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.03) inset',
    }}>
      <HeroPhotoPanel
        heroImage={d.hero_image}
        badge={`✅ ${d.category || 'CHECKLIST'}`}
        accentColor={C.accent} accentFade={C.accentFade} accentBdr={C.accentBorder}
        placeholder="✅" placeholderLabel="Checklist"
        title={d.title}
      />
      <div style={{ flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
        <div>
          {(sm || pm || d.due_date) && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {sm && <Pill label={sm.label} color={sm.color} />}
              {pm && <Pill label={`${pm.label} prioridade`} color={pm.color} />}
              {d.due_date && <Pill label={`Prazo: ${d.due_date}`} color={C.warning} />}
            </div>
          )}
          <h1 style={{ margin: '0 0 8px', fontSize: titleFs, fontWeight: 800, color: C.text, lineHeight: 1.2, letterSpacing: '-.02em',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.title}</h1>
          {d.subtitle && <p style={{ margin: 0, fontSize: 13, color: C.textMuted, lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.subtitle}</p>}
        </div>
        <MetricStrip metrics={METRICS} accentColor={C.accent} />
      </div>
    </div>
  );
}

// ── Hero Mobile ──────────────────────────────────────────────────────────────
function ChecklistHeroMobile({ d, checkedCount, total }) {
  const pct = total > 0 ? Math.round((checkedCount / total) * 100) : 0;
  const pm  = d.priority && PRIORITY_META[d.priority];
  const METRICS = [
    { icon: '📋', value: total,         label: 'Total'      },
    { icon: '✅', value: checkedCount,  label: 'Feitos'     },
    { icon: '📊', value: `${pct}%`,     label: 'Completo'   },
    { icon: '⚡', value: pm?.label || (d.due_date ? d.due_date : 'Normal'), label: pm ? 'Prioridade' : 'Prazo' },
  ];

  return (
    <div style={{ background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)', border: `1px solid ${C.border}`,
      borderRadius: 12, overflow: 'hidden', marginBottom: 14,
      boxShadow: '0 4px 40px rgba(0,0,0,0.4)',
    }}>
      <div style={{ display: 'flex', minHeight: 202 }}>
        <div style={{ flex: 1, minWidth: 0, padding: '20px 10px 18px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <span style={{ display: 'inline-block', alignSelf: 'flex-start', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: C.accent, background: C.accentFade, border: `1px solid ${C.accentBorder}`, borderRadius: 5, padding: '2px 8px', marginBottom: 10 }}>✅ {d.category || 'CHECKLIST'}</span>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.text, lineHeight: 1.25 }}>{d.title}</h1>
          {d.subtitle && <p style={{ margin: '6px 0 0', fontSize: 12, color: C.textMuted, lineHeight: 1.4,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.subtitle}</p>}
        </div>
        <HeroPhotoPanel
          heroImage={d.hero_image} isMobile
          accentColor={C.accent} accentFade={C.accentFade} accentBdr={C.accentBorder}
          placeholder="✅"
          title={d.title}
        />
      </div>
      <div style={{ borderTop: `1px solid ${C.border}`, background: 'rgba(0,0,0,0.18)' }}>
        <MetricStrip metrics={METRICS} bordered accentColor={C.accent} />
      </div>
    </div>
  );
}

// ── ProgressBar ──────────────────────────────────────────────────────────────
function ProgressBar({ checkedCount, total }) {
  if (total === 0) return null;
  const pct   = Math.round((checkedCount / total) * 100);
  const pending = total - checkedCount;

  let barColor = C.accent;
  let statusText = 'Em andamento';
  if (pct === 0)        { barColor = C.textFaint; statusText = 'Não iniciado'; }
  else if (pct >= 100)  { barColor = C.success;   statusText = 'Concluído ✓'; }
  else if (pct >= 80)   { barColor = C.success;   statusText = 'Quase concluído'; }

  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      padding: '14px 18px',
      marginBottom: 14,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 10,
      }}>
        <span style={{ fontSize: 12, color: C.textMuted, letterSpacing: '.04em' }}>
          Progresso
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: barColor, fontWeight: 600 }}>{statusText}</span>
          <span style={{
            fontSize: 16, fontWeight: 800, color: barColor,
            fontVariantNumeric: 'tabular-nums',
          }}>{pct}%</span>
        </div>
      </div>

      {/* Track */}
      <div style={{
        height: 5, background: 'rgba(14,22,40,0.55)',
        borderRadius: 3, overflow: 'hidden',
      }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: barColor, borderRadius: 3,
          transition: 'width .4s cubic-bezier(.4,0,.2,1)',
          minWidth: pct > 0 ? 4 : 0,
        }} />
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 18, marginTop: 10, fontSize: 12 }}>
        <span>
          <strong style={{ color: C.success, fontVariantNumeric: 'tabular-nums' }}>
            {checkedCount}
          </strong>
          <span style={{ color: C.textFaint }}> concluídos</span>
        </span>
        {pending > 0 && (
          <span>
            <strong style={{ color: C.textMuted, fontVariantNumeric: 'tabular-nums' }}>
              {pending}
            </strong>
            <span style={{ color: C.textFaint }}> pendentes</span>
          </span>
        )}
      </div>
    </div>
  );
}

// ── FilterBar ────────────────────────────────────────────────────────────────
function FilterBar({ filter, onChange, counts, onMarkAll, onUnmarkAll }) {
  const tabs = [
    { id: 'all',      label: 'Todos',        n: counts.all      },
    { id: 'pending',  label: 'Pendentes',    n: counts.pending  },
    { id: 'done',     label: 'Concluídos',   n: counts.done     },
    { id: 'required', label: 'Obrigatórios', n: counts.required },
  ].filter(t => t.n > 0 || t.id === 'all');

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      marginBottom: 16, flexWrap: 'wrap',
    }}>
      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 5, overflowX: 'auto', flex: 1, minWidth: 0 }}>
        {tabs.map(t => {
          const active = filter === t.id;
          return (
            <button
              key={t.id}
              onClick={() => onChange(t.id)}
              aria-pressed={active}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 11px',
                background: active ? C.accentFade : 'transparent',
                border: `1px solid ${active ? C.accent : C.border}`,
                borderRadius: 20,
                color: active ? C.accent : C.textMuted,
                fontSize: 12, fontWeight: active ? 700 : 400,
                cursor: 'pointer', whiteSpace: 'nowrap',
                transition: 'all .15s', outline: 'none',
              }}
            >
              {t.label}
              {t.n > 0 && (
                <span style={{
                  fontSize: 10, lineHeight: '16px',
                  background: active ? C.accent : 'rgba(56,189,248,0.12)',
                  color: active ? 'rgba(14,22,40,0.9)' : C.textFaint,
                  borderRadius: 10, padding: '0 5px', fontWeight: 700,
                }}>{t.n}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Quick actions */}
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <button
          onClick={onMarkAll}
          title="Marcar todos"
          style={{
            fontSize: 11, padding: '4px 10px',
            background: 'transparent',
            border: `1px solid ${C.border}`,
            borderRadius: 6, color: C.textMuted,
            cursor: 'pointer', outline: 'none',
          }}
        >✓ Todos</button>
        <button
          onClick={onUnmarkAll}
          title="Desmarcar todos"
          style={{
            fontSize: 11, padding: '4px 10px',
            background: 'transparent',
            border: `1px solid ${C.border}`,
            borderRadius: 6, color: C.textMuted,
            cursor: 'pointer', outline: 'none',
          }}
        >○ Limpar</button>
      </div>
    </div>
  );
}

// ── ChecklistItem ────────────────────────────────────────────────────────────
function ChecklistItem({ item, checked, onToggle }) {
  const isBlocked  = item.blocked;
  const isRequired = item.required;
  const isCritical = item.priority === 'critical';

  const bg = isBlocked  ? C.dangerFade
           : checked    ? C.successFade
           : 'transparent';

  const borderColor = isBlocked || isCritical ? `${C.danger}28`
                    : isRequired              ? `${C.warning}22`
                    : 'transparent';

  const checkColor = checked ? C.success
                   : isRequired ? C.warning
                   : C.textFaint;

  return (
    <div
      role="checkbox"
      aria-checked={checked}
      aria-label={item.text}
      tabIndex={isBlocked ? -1 : 0}
      onClick={() => !isBlocked && onToggle(item.id)}
      onKeyDown={e => {
        if ((e.key === ' ' || e.key === 'Enter') && !isBlocked) {
          e.preventDefault();
          onToggle(item.id);
        }
      }}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 11,
        padding: '9px 11px',
        background: bg,
        border: `1px solid ${borderColor}`,
        borderRadius: 8,
        cursor: isBlocked ? 'not-allowed' : 'pointer',
        transition: 'background .15s, border-color .15s',
        minHeight: 44,
        userSelect: 'none', outline: 'none',
      }}
    >
      {/* Checkbox */}
      <div style={{
        width: 20, height: 20, borderRadius: 5, flexShrink: 0,
        marginTop: 1,
        border: `2px solid ${checked ? C.success : checkColor}`,
        background: checked ? C.successFade : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all .2s',
      }}>
        {checked && (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden="true">
            <path
              d="M1 4l3 3 5-6"
              stroke={C.success} strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"
            />
          </svg>
        )}
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          fontSize: 14, lineHeight: 1.5,
          color: checked  ? C.textFaint
               : isBlocked ? C.danger
               : C.text,
          textDecoration: checked ? 'line-through' : 'none',
        }}>{item.text}</span>
        {item.note && (
          <div style={{ fontSize: 12, color: C.textFaint, marginTop: 3, lineHeight: 1.4 }}>
            {item.note}
          </div>
        )}
      </div>

      {/* Badges */}
      <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'flex-start', paddingTop: 2 }}>
        {isRequired && !checked && (
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '.04em',
            color: C.warning, background: C.warningFade,
            border: `1px solid rgba(245,158,11,0.22)`,
            borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap',
          }}>✦ Obrig.</span>
        )}
        {isBlocked && (
          <span style={{
            fontSize: 10, fontWeight: 700,
            color: C.danger, background: C.dangerFade,
            border: `1px solid ${C.dangerBorder}`,
            borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap',
          }}>⚠ Bloq.</span>
        )}
      </div>
    </div>
  );
}

// ── GroupSection ─────────────────────────────────────────────────────────────
function GroupSection({ group, checkedIds, onToggle, filter, groupIdx }) {
  const [open, setOpen] = useState(true);

  const visibleItems = useMemo(() => {
    const items = group.items || [];
    switch (filter) {
      case 'pending':  return items.filter(it => !checkedIds.has(it.id));
      case 'done':     return items.filter(it => checkedIds.has(it.id));
      case 'required': return items.filter(it => it.required);
      default:         return items;
    }
  }, [group.items, checkedIds, filter]);

  if (visibleItems.length === 0) return null;

  const checkedInGroup = visibleItems.filter(it => checkedIds.has(it.id)).length;
  const totalInGroup   = visibleItems.length;
  const allDone        = checkedInGroup === totalInGroup;
  const progressPct    = totalInGroup > 0 ? Math.round((checkedInGroup / totalInGroup) * 100) : 0;

  const hasLabel = group.label && group.label.trim();

  return (
    <div style={{ marginBottom: 10 }}>
      {hasLabel && (
        <button
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 8,
            padding: '9px 12px',
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: open ? '8px 8px 0 0' : 8,
            cursor: 'pointer',
            color: C.text, outline: 'none',
            textAlign: 'left',
            transition: 'border-radius .15s',
          }}
        >
          {group.icon && <span style={{ fontSize: 15 }}>{group.icon}</span>}
          <span style={{ flex: 1, fontSize: 13, fontWeight: 700, letterSpacing: '.04em' }}>
            {group.label}
          </span>

          {/* Group progress */}
          <span style={{
            fontSize: 11, color: allDone ? C.success : C.textFaint,
            fontVariantNumeric: 'tabular-nums',
            marginRight: 6,
          }}>{checkedInGroup}/{totalInGroup}</span>

          {/* Mini bar */}
          <div style={{
            width: 36, height: 4, background: 'rgba(14,22,40,0.4)',
            borderRadius: 2, overflow: 'hidden', flexShrink: 0,
          }}>
            <div style={{
              height: '100%', width: `${progressPct}%`,
              background: allDone ? C.success : C.accent,
              borderRadius: 2, transition: 'width .3s',
            }} />
          </div>

          {/* Chevron */}
          <span style={{
            fontSize: 10, color: C.textFaint,
            display: 'block', marginLeft: 4,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform .2s',
          }}>▾</span>
        </button>
      )}

      {open && (
        <div style={{
          border: `1px solid ${C.border}`,
          borderTop: hasLabel ? 'none' : `1px solid ${C.border}`,
          borderRadius: hasLabel ? '0 0 8px 8px' : 8,
          overflow: 'hidden',
          padding: 6,
          display: 'flex', flexDirection: 'column', gap: 3,
        }}>
          {visibleItems.map((item) => (
            <ChecklistItem
              key={item.id}
              item={item}
              checked={checkedIds.has(item.id)}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── EmptyState ────────────────────────────────────────────────────────────────
function EmptyState({ filter }) {
  const map = {
    all:      { icon: '📋', text: 'Checklist vazio. Nenhum item encontrado.' },
    pending:  { icon: '🎉', text: 'Tudo concluído! Você não tem pendências neste checklist.' },
    done:     { icon: '○', text: 'Nenhum item concluído ainda.' },
    required: { icon: '✓', text: 'Nenhum item marcado como obrigatório.' },
  };
  const m = map[filter] || map.all;
  return (
    <div style={{
      padding: '32px 20px', textAlign: 'center',
      border: `1px dashed ${C.border}`, borderRadius: 10,
      marginBottom: 16,
    }}>
      <div style={{ fontSize: 26, marginBottom: 8 }}>{m.icon}</div>
      <div style={{ fontSize: 14, color: C.textMuted }}>{m.text}</div>
    </div>
  );
}

// ── CriticalPanel ─────────────────────────────────────────────────────────────
function CriticalPanel({ criticalItems, checkedIds }) {
  const active = (criticalItems || []).filter(it => !checkedIds.has(it.id));
  if (active.length === 0) return null;
  return (
    <div style={{
      background: C.dangerFade,
      border: `1px solid ${C.dangerBorder}`,
      borderRadius: 10,
      padding: '14px 16px',
      marginBottom: 16,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 800, letterSpacing: '.08em',
        color: C.danger, textTransform: 'uppercase', marginBottom: 10,
      }}>⚠ Pendências críticas ({active.length})</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {active.map(it => (
          <div key={it.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{ color: C.danger, flexShrink: 0, marginTop: 2 }}>•</span>
            <span style={{ fontSize: 13, color: C.text, flex: 1 }}>{it.text}</span>
            {it.group && (
              <span style={{
                fontSize: 10, color: C.textFaint,
                background: 'rgba(14,22,40,0.4)',
                padding: '1px 6px', borderRadius: 4, flexShrink: 0,
              }}>{it.group}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ChecklistShowcase({ data, wp, onAction }) {
  const d = wp || data;

  // Hooks must precede any early return (Rules of Hooks)
  const isMobile = usePresentationBreakpoint();

  const allItems = useMemo(() => {
    const out = [];
    ((d?.groups) || []).forEach(g => (g.items || []).forEach(it => out.push(it)));
    return out;
  }, [d]);

  const [checkedIds, setCheckedIds] = useState(() => {
    const ids = new Set();
    allItems.forEach(it => { if (it.checked) ids.add(it.id); });
    return ids;
  });

  const [filter, setFilter] = useState('all');

  const toggle = useCallback((id) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const markAll = useCallback(() => {
    setCheckedIds(new Set(allItems.map(it => it.id)));
  }, [allItems]);

  const unmarkAll = useCallback(() => {
    setCheckedIds(new Set());
  }, []);

  const total        = allItems.length;
  const checkedCount = checkedIds.size;

  const counts = useMemo(() => ({
    all:      total,
    pending:  allItems.filter(it => !checkedIds.has(it.id)).length,
    done:     checkedCount,
    required: allItems.filter(it => it.required).length,
  }), [allItems, checkedIds, total, checkedCount]);

  const groups        = d?.groups        || [];
  const criticalItems = d?.critical_items || [];

  const hasVisibleItems = useMemo(() => (d?.groups || []).some(g => {
    const items = g.items || [];
    switch (filter) {
      case 'pending':  return items.some(it => !checkedIds.has(it.id));
      case 'done':     return items.some(it => checkedIds.has(it.id));
      case 'required': return items.some(it => it.required);
      default:         return items.length > 0;
    }
  }), [d, checkedIds, filter]);

  if (!d) return null;

  const sharedContent = (
    <>
      <ProgressBar checkedCount={checkedCount} total={total} />

      <FilterBar
        filter={filter}
        onChange={setFilter}
        counts={counts}
        onMarkAll={markAll}
        onUnmarkAll={unmarkAll}
      />

      {criticalItems.length > 0 && (
        <CriticalPanel criticalItems={criticalItems} checkedIds={checkedIds} />
      )}

      {hasVisibleItems ? (
        groups.map((g, gi) => (
          <GroupSection
            key={g.id || gi}
            group={g}
            checkedIds={checkedIds}
            onToggle={toggle}
            filter={filter}
            groupIdx={gi}
          />
        ))
      ) : (
        <EmptyState filter={filter} />
      )}

      <AthenaTip tip={d.tip} accentColor={C.warning} style={{ marginBottom: 16 }} />

      <ActionChips chips={d.follow_up_chips} onAction={onAction} accentColor={C.accent} />
    </>
  );

  // ── Desktop layout ──────────────────────────────────────────────────────────
  if (!isMobile) {
    return (
      <div style={{
        color: C.text, fontFamily: 'inherit',
        lineHeight: 1.6, maxWidth: '100%',
      }}>
        {/* Athena intro */}
        {d.context && (
          <p style={{
            fontSize: 14, color: C.textMuted,
            marginBottom: 18, lineHeight: 1.6,
          }}>{d.context}</p>
        )}

        <ChecklistHeroDesktop d={d} checkedCount={checkedCount} total={total} />

        {sharedContent}
      </div>
    );
  }

  // ── Mobile layout ───────────────────────────────────────────────────────────
  return (
    <div style={{
      color: C.text, fontFamily: 'inherit',
      lineHeight: 1.6, maxWidth: '100%',
    }}>
      {d.context && (
        <p style={{
          fontSize: 13, color: C.textMuted,
          marginBottom: 14, lineHeight: 1.5,
        }}>{d.context}</p>
      )}

      <ChecklistHeroMobile d={d} checkedCount={checkedCount} total={total} />

      {sharedContent}
    </div>
  );
}
